import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUserTie,
    faGraduationCap,
    faBriefcase,
    faBuilding,
    faAward,
    faUsers,
    faCertificate,
    faBook,
    faScaleBalanced,
    faGlobe,
    faHandshake,
    faUniversity,
    faStar,
    faChevronDown,
    faChevronUp,
    faEnvelope,
    faPhone,
    faMapMarkerAlt,
    faCalendarAlt,
    faGavel,
    faRibbon
} from "@fortawesome/free-solid-svg-icons";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const partners = [
    {
        id: 1,
        name: "NIKHIL KUMAR AGRAWAL",
        designation: "Advocate-on-Record, Patna High Court",
        image: "nikhil.jpeg",
        color: "primary-blue",
        qualifications: [
            {
                degree: "LL.M., University College London (UCL)",
                details: "Bentham Scholarship 2008-09 awarded by UCL"
            },
            {
                degree: "B.B.A., LL.B., Symbiosis Law School, Pune",
                details: ""
            }
        ],
        experience: "Since August 2008 (18+ years)",
        pastAssociations: [
            "Dastur Dadhich & Kalambi, Mumbai",
            "Bharucha & Partners, Mumbai",
            "Mulla & Mulla & Craigie Blunt & Caroe, Mumbai",
            "Paras Kuhad & Associates, Mumbai",
            "Singhania and Co. LLP, New Delhi"
        ],
        professionalAssociations: [
            "Member, Bar Council of India",
            "Member, Bihar State Bar Council",
            "Assistant Counsel, State of Bihar (2013-2018)",
            // "Retained Counsel, IndiGo Airlines",
            "Retained Counsel, Official Liquidator, Patna High Court",
            "Panel Advocate, Bhimrao Ambedkar Bihar University (2016-present)",
            "Panel Advocate, Bihar State Financial Corporation (2019-present)"
        ],
        additionalQualifications: [
            "Diploma in Corporate Mergers, Acquisitions and Restructuring",
            "General Course on IP from WIPO, Geneva",
            "Diploma in Intellectual Property Laws",
            "Diploma in International Commercial Arbitration",
            "Diploma in Consumer Protection Laws"
        ],
        specialties: ["Corporate Law", "Civil Litigation", "IP Law", "Arbitration"],
        email: "rnalawchambers@gmail.com",
        phone: "+919473425998",
        yearsOfExperience: 18
    },
    {
        id: 2,
        name: "ADITI HANSARIA",
        designation: "Advocate-on-Record, Patna High Court",
        image: "hans.jpeg",
        color: "primary-blue",
        qualifications: [
            {
                degree: "LL.M., London School of Economics & Political Science (LSE)",
                details: "Marchant Foundation LL.M Scholarship awarded by LSE"
            },
            {
                degree: "B.L.S., LL.B., Government Law College, Mumbai",
                details: ""
            }
        ],
        experience: "Since August 2008 (18+ years)",
        pastAssociations: [
            "Chir Amrit Law Chambers, Jaipur ",
            "M/s. M. Dhruva & Partners, Mumbai",
            "Judicial Intern, Hon'ble Mr. Justice D.B. Bhosale, Bombay High Court",
            "M/s. Manilal Kher Ambalal & Co., Mumbai"
        ],
        professionalAssociations: [
            "Member, Bar Council of India",
            "Member, Bihar State Bar Council",
            "Assistant Counsel, State of Bihar (2013-2018)",
            "Retained Counsel, BPCL"
        ],
        additionalQualifications: [],
        specialties: ["Civil Law", "Constitutional Law", "Commercial Disputes"],
        email: "rnalawchambers@gmail.com",
        phone: "+9194734 25998",
        yearsOfExperience: 18
    }
];

const HoverCard = ({ children, className = "", style = {}, ...props }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            className={`position-relative overflow-hidden ${className}`}
            style={style}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            whileHover={{
                y: -8,
                transition: { duration: 0.3, ease: "easeOut" }
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            {...props}
        >
            {/* Hover glow effect */}
            <motion.div
                className="position-absolute top-0 left-0 w-full h-full"
                initial={false}
                animate={{
                    opacity: isHovered ? 0.1 : 0,
                    scale: isHovered ? 1.05 : 1
                }}
                transition={{ duration: 0.3 }}
                style={{
                    background: "radial-gradient(circle at center, var(--gold) 0%, transparent 70%)",
                    zIndex: 0
                }}
            />

            {children}
        </motion.div>
    );
};

const SectionCard = ({ icon, title, children, color = "gold", className = "" }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    return (
        <HoverCard
            className={`rounded-4 ${className}`}
            style={{
                backgroundColor: "var(--white)",
                border: `1px solid var(--${color}-light)`,
                boxShadow: "var(--shadow-soft)"
            }}
        >
            <div className="p-4 position-relative z-1">
                <div
                    className="d-flex align-items-center justify-content-between mb-3 cursor-pointer"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div className="p-2 rounded-3"
                            style={{
                                backgroundColor: `rgba(var(--${color}-rgb), 0.1)`,
                                color: `var(--${color})`
                            }}
                        >
                            <FontAwesomeIcon icon={icon} />
                        </div>
                        <h5 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                            {title}
                        </h5>
                    </div>
                    <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ color: `var(--${color})` }}
                    >
                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                    </motion.div>
                </div>

                <motion.div
                    initial={false}
                    animate={{
                        height: isExpanded ? "auto" : 0,
                        opacity: isExpanded ? 1 : 0
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    style={{ overflow: "hidden" }}
                >
                    {children}
                </motion.div>
            </div>
        </HoverCard>
    );
};

const QualificationItem = ({ item }) => (
    <motion.div
        className="mb-3"
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
    >
        <div className="d-flex gap-3">
            <div className="flex-shrink-0">
                <div className="p-1 rounded-circle"
                    style={{
                        backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                        color: "var(--gold)",
                        width: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    •
                </div>
            </div>
            <div>
                <div className="fw-medium" style={{ color: "var(--primary-dark)" }}>
                    {item.degree}
                </div>
                {item.details && (
                    <div className="small mt-1" style={{ color: "var(--gold)" }}>
                        {item.details}
                    </div>
                )}
            </div>
        </div>
    </motion.div>
);

const ListItem = ({ item, icon, color = "gold" }) => (
    <motion.div
        className="d-flex align-items-start gap-3 mb-2"
        whileHover={{ x: 5 }}
        transition={{ type: "spring", stiffness: 400 }}
    >
        <FontAwesomeIcon
            icon={icon}
            className="mt-1 flex-shrink-0"
            style={{
                color: `var(--${color})`,
                fontSize: "0.8rem",
                minWidth: "16px"
            }}
        />
        <div className="small" style={{ color: "var(--text-primary)", lineHeight: 1.5 }}>
            {item}
        </div>
    </motion.div>
);

export const OurTeam = () => {
    const [activePartner, setActivePartner] = useState(0);
    const [expandedSections, setExpandedSections] = useState({
        qualifications: true,
        experience: true,
        associations: true,
        additional: true
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    return (
        <div style={{
            backgroundColor: "var(--cream)",
            minHeight: "100vh",
            padding: "130px 0 60px",
            position: "relative"
        }}>
            {/* Background decorative elements */}
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
                {/* Floating particles */}
                {[...Array(20)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="position-absolute"
                        style={{
                            width: "3px",
                            height: "3px",
                            backgroundColor: "var(--gold-light)",
                            borderRadius: "50%",
                            opacity: 0.1,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`
                        }}
                        animate={{
                            y: [0, -20, 0],
                            x: [0, Math.random() * 10 - 5, 0]
                        }}
                        transition={{
                            duration: 3 + Math.random() * 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: Math.random() * 2
                        }}
                    />
                ))}
            </div>

            <div className="container position-relative" style={{ zIndex: 1 }}>
                {/* Hero Section */}
                <motion.div
                    className="text-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <motion.div variants={fadeInUp}>
                        <span style={{
                            color: "var(--gold)",
                            fontWeight: "600",
                            letterSpacing: "3px",
                            fontSize: "0.9rem",
                            textTransform: "uppercase",
                            display: "inline-block",
                            padding: "8px 20px",
                            border: "1px solid var(--gold-light)",
                            borderRadius: "50px",
                            background: "rgba(184, 134, 11, 0.05)",
                            backdropFilter: "blur(4px)",
                            marginTop: 8
                        }}>
                            Legal Excellence
                        </span>

                        <motion.h4
                            className="fw-bold display-6 display-md-5"
                            variants={fadeInUp}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontWeight: 600,
                                color: 'var(--primary-dark)',
                                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}
                        >
                            Meet Our Expert Team
                        </motion.h4>
                    </motion.div>

                    <motion.p
                        className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "750px",
                            color: "var(--text-primary)",
                            lineHeight: 1.7
                        }}
                    >
                        Led by London-educated Advocates-on-Record with over 18 years of experience,
                        our team combines international legal education with deep understanding of Indian jurisprudence.
                    </motion.p>
                </motion.div>

                {/* Partner Selection */}
                <motion.div
                    className="row justify-content-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="col-lg-8">
                        <div className="d-flex flex-wrap justify-content-center gap-3">
                            {partners.map((partner, index) => (
                                <motion.button
                                    key={partner.id}
                                    className={`btn px-4 py-3 rounded-pill d-flex align-items-center gap-3 ${activePartner === index ? 'active' : ''}`}
                                    onClick={() => setActivePartner(index)}
                                    variants={itemVariants}
                                    style={{
                                        backgroundColor: activePartner === index ? `var(--${partner.color})` : "transparent",
                                        color: activePartner === index ? "var(--white)" : `var(--${partner.color})`,
                                        border: `2px solid var(--${partner.color})`,
                                        fontWeight: "600",
                                        transition: "all 0.3s ease",
                                        minWidth: "250px"
                                    }}
                                    whileHover={{
                                        scale: 1.05,
                                        backgroundColor: activePartner === index ? `var(--${partner.color}-light)` : `rgba(var(--${partner.color}-rgb), 0.1)`
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {/* <span className="fs-4">{partner.image}</span> */}
                                    <span>{partner.name.split(' ')[0]}</span>
                                </motion.button>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Main Content */}
                <motion.div
                    className="row g-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    {/* Left Column - Partner Profile */}
                    <div className="col-lg-4">
                        <motion.div variants={itemVariants}>
                            <HoverCard
                                className="rounded-4 overflow-hidden"
                                style={{
                                    backgroundColor: "var(--white)",
                                    border: `2px solid var(--${partners[activePartner].color})`,
                                    boxShadow: "var(--shadow-medium)"
                                }}
                            >
                                {/* Profile Header */}
                                <div
                                    className="p-5 text-center position-relative overflow-hidden d-flex flex-column justify-content-end"
                                    style={{
                                        backgroundImage: `url(${partners[activePartner].image})`,
                                        backgroundSize: "cover",                                       
                                        backgroundRepeat: "no-repeat",
                                        minHeight: "420px",
                                    }}
                                >
                                    {/* Overlay */}
                                    <div
                                        className="position-absolute top-0 start-0 w-100 h-100"
                                        style={{
                                            background:
                                                "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.18) 0%, rgba(0,0,0,0.35) 60%)",
                                            zIndex: 0,
                                        }}
                                    />

                                    {/* Bottom content */}
                                    <div className="position-relative z-1">
                                        <h3 className="fw-bold mb-2 text-white">
                                            {partners[activePartner].name}
                                        </h3>

                                        <div
                                            className="badge px-3 py-2 mb-3"
                                            style={{
                                                backgroundColor: "rgba(255, 255, 255, 0.25)",
                                                color: "#fff",
                                                border: "1px solid rgba(255, 255, 255, 0.35)",
                                                fontSize: "0.7rem",
                                                backdropFilter: "blur(8px)",
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faUserTie} className="me-2" />
                                            {partners[activePartner].designation}
                                        </div>

                                        <motion.div
                                            className="text-center"
                                            whileHover={{ scale: 1.08 }}
                                            transition={{ type: "spring", stiffness: 200 }}
                                        >
                                            <div className="display-7 fw-bold text-white">
                                                {partners[activePartner].yearsOfExperience}+
                                            </div>
                                            <div
                                                style={{
                                                    color: "rgba(255, 255, 255, 0.85)",
                                                    fontSize: "0.7rem",
                                                }}
                                            >
                                                Years Experience
                                            </div>
                                        </motion.div>
                                    </div>
                                </div>



                                {/* Contact Info */}
                                <div className="p-4">
                                    <div className="vstack gap-3">
                                        <motion.a
                                            href={`mailto:${partners[activePartner].email}`}
                                            className="d-flex align-items-center gap-3 p-3 rounded-3 text-decoration-none"
                                            style={{
                                                backgroundColor: "rgba(var(--gold-rgb), 0.05)",
                                                border: "1px solid var(--gold-light)"
                                            }}
                                            whileHover={{
                                                x: 5,
                                                backgroundColor: "rgba(var(--gold-rgb), 0.1)"
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faEnvelope} style={{ color: "var(--gold)" }} />
                                            <div>
                                                <div className="small" style={{ color: "var(--gray-medium)" }}>Email</div>
                                                <div className="fw-medium" style={{ color: "var(--primary-dark)" }}>
                                                    {partners[activePartner].email}
                                                </div>
                                            </div>
                                        </motion.a>

                                        <motion.div
                                            className="d-flex align-items-center gap-3 p-3 rounded-3"
                                            style={{
                                                backgroundColor: "rgba(var(--primary-blue-rgb), 0.05)",
                                                border: "1px solid var(--primary-blue)"
                                            }}
                                            whileHover={{ x: 5 }}
                                        >
                                            <FontAwesomeIcon icon={faPhone} style={{ color: "var(--primary-blue)" }} />
                                            <div>
                                                <div className="small" style={{ color: "var(--gray-medium)" }}>Phone</div>
                                                <div className="fw-medium" style={{ color: "var(--primary-dark)" }}>
                                                    {partners[activePartner].phone}
                                                </div>
                                            </div>
                                        </motion.div>

                                        <div className="d-flex align-items-center gap-3 p-3 rounded-3"
                                            style={{
                                                backgroundColor: "rgba(var(--gray-medium-rgb), 0.05)",
                                                border: "1px solid var(--gray-light)"
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faCalendarAlt} style={{ color: "var(--gray-medium)" }} />
                                            <div>
                                                <div className="small" style={{ color: "var(--gray-medium)" }}>Experience Since</div>
                                                <div className="fw-medium" style={{ color: "var(--primary-dark)" }}>
                                                    August 2008
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Specialties */}
                                    <div className="mt-4 pt-4 border-top"
                                        style={{ borderColor: "var(--gold-light)" }}
                                    >
                                        <h6 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                            Areas of Specialization
                                        </h6>
                                        <div className="d-flex flex-wrap gap-2">
                                            {partners[activePartner].specialties.map((specialty, index) => (
                                                <motion.span
                                                    key={index}
                                                    className="badge px-3 py-2"
                                                    style={{
                                                        backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                        color: "var(--gold)",
                                                        border: "1px solid var(--gold-light)",
                                                        fontSize: "0.85rem"
                                                    }}
                                                    whileHover={{
                                                        scale: 1.1,
                                                        backgroundColor: "rgba(var(--gold-rgb), 0.2)"
                                                    }}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    whileInView={{ opacity: 1, y: 0 }}
                                                    viewport={{ once: true }}
                                                    transition={{ delay: index * 0.1 }}
                                                >
                                                    {specialty}
                                                </motion.span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </HoverCard>
                        </motion.div>
                    </div>

                    {/* Right Column - Detailed Information */}
                    <div className="col-lg-8">
                        <div className="vstack gap-4">
                            {/* Qualifications */}
                            <SectionCard
                                icon={faGraduationCap}
                                title="Academic Qualifications"
                                color={partners[activePartner].color}
                            >
                                <div className="mt-3">
                                    {partners[activePartner].qualifications.map((qualification, index) => (
                                        <QualificationItem key={index} item={qualification} />
                                    ))}

                                    {/* Scholarships */}
                                    <motion.div
                                        className="mt-4 p-3 rounded-3"
                                        style={{
                                            backgroundColor: "rgba(var(--gold-rgb), 0.05)",
                                            border: "1px dashed var(--gold-light)"
                                        }}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.2 }}
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            <FontAwesomeIcon icon={faAward} style={{ color: "var(--gold)", fontSize: "1.2rem" }} />
                                            <div>
                                                <div className="fw-bold" style={{ color: "var(--gold)" }}>
                                                    Scholarship Awards
                                                </div>
                                                <div className="small" style={{ color: "var(--text-primary)" }}>
                                                    {partners[activePartner].qualifications[0].details}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </SectionCard>

                            {/* Professional Experience */}
                            <SectionCard
                                icon={faBriefcase}
                                title="Professional Experience"
                                color={partners[activePartner].color}
                            >
                                <div className="mt-3">
                                    <motion.div
                                        className="mb-4 p-3 rounded-3"
                                        style={{
                                            backgroundColor: "rgba(var(--primary-blue-rgb), 0.05)",
                                            borderLeft: "4px solid var(--primary-blue)"
                                        }}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                    >
                                        <div className="d-flex align-items-center gap-3 mb-2">
                                            <FontAwesomeIcon icon={faCalendarAlt} style={{ color: "var(--primary-blue)" }} />
                                            <div className="fw-bold" style={{ color: "var(--primary-dark)" }}>
                                                {partners[activePartner].experience}
                                            </div>
                                        </div>
                                        <div className="small" style={{ color: "var(--text-primary)" }}>
                                            Comprehensive experience in litigation, advisory, and dispute resolution
                                        </div>
                                    </motion.div>

                                    <h6 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                        Past Professional Associations
                                    </h6>
                                    <div className="row g-2">
                                        {partners[activePartner].pastAssociations.map((association, index) => (
                                            <motion.div
                                                key={index}
                                                className="col-12 col-md-6"
                                                initial={{ opacity: 0, y: 10 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: index * 0.1 }}
                                            >
                                                <ListItem item={association} icon={faBuilding} color="gold" />
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Professional Associations */}
                            <SectionCard
                                icon={faUsers}
                                title="Professional Associations"
                                color={partners[activePartner].color}
                            >
                                <div className="mt-3">
                                    <div className="row g-3">
                                        {partners[activePartner].professionalAssociations.map((association, index) => (
                                            <motion.div
                                                key={index}
                                                className="col-12 col-md-6"
                                                initial={{ opacity: 0, y: 10 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: index * 0.1 }}
                                            >
                                                <div className="d-flex align-items-start gap-3 p-3 rounded-3 h-100"
                                                    style={{
                                                        backgroundColor: index < 2 ? "rgba(var(--gold-rgb), 0.05)" : "rgba(var(--primary-blue-rgb), 0.03)",
                                                        border: "1px solid var(--gold-light)"
                                                    }}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={index < 2 ? faScaleBalanced : faHandshake}
                                                        className="mt-1 flex-shrink-0"
                                                        style={{
                                                            color: index < 2 ? "var(--gold)" : "var(--primary-blue)",
                                                            fontSize: "0.9rem"
                                                        }}
                                                    />
                                                    <div className="small" style={{ color: "var(--text-primary)", lineHeight: 1.5 }}>
                                                        {association}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Additional Qualifications (only for Partner 1) */}
                            {partners[activePartner].id === 1 && partners[activePartner].additionalQualifications.length > 0 && (
                                <SectionCard
                                    icon={faCertificate}
                                    title="Additional Qualifications & Certifications"
                                    color={partners[activePartner].color}
                                >
                                    <div className="mt-3">
                                        <div className="row g-2">
                                            {partners[activePartner].additionalQualifications.map((qualification, index) => (
                                                <motion.div
                                                    key={index}
                                                    className="col-12 col-md-6"
                                                    initial={{ opacity: 0, x: -20 }}
                                                    whileInView={{ opacity: 1, x: 0 }}
                                                    viewport={{ once: true }}
                                                    transition={{ delay: index * 0.1 }}
                                                >
                                                    <div className="d-flex align-items-start gap-3 p-3 rounded-3 h-100"
                                                        style={{
                                                            backgroundColor: "rgba(var(--gold-rgb), 0.03)",
                                                            border: "1px solid var(--gold-light)"
                                                        }}
                                                    >
                                                        <div className="flex-shrink-0">
                                                            <div className="p-1 rounded-circle d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                                    color: "var(--gold)",
                                                                    width: "20px",
                                                                    height: "20px"
                                                                }}
                                                            >
                                                                ✓
                                                            </div>
                                                        </div>
                                                        <div className="small" style={{ color: "var(--text-primary)", lineHeight: 1.5 }}>
                                                            {qualification}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>

                                        {/* International Certification Highlight */}
                                        <motion.div
                                            className="mt-4 p-4 rounded-3"
                                            style={{
                                                backgroundColor: "rgba(var(--gold-rgb), 0.05)",
                                                border: "2px solid var(--gold-light)"
                                            }}
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            whileInView={{ opacity: 1, scale: 1 }}
                                            viewport={{ once: true }}
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <FontAwesomeIcon icon={faGlobe} style={{ color: "var(--gold)", fontSize: "1.5rem" }} />
                                                <div>
                                                    <div className="fw-bold" style={{ color: "var(--gold)" }}>
                                                        International Certification
                                                    </div>
                                                    <div className="small" style={{ color: "var(--text-primary)" }}>
                                                        General Course on IP from WIPO, Geneva - World Intellectual Property Organization
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </div>
                                </SectionCard>
                            )}

                            {/* Recognition & Awards */}
                            <motion.div
                                className="p-4 rounded-4"
                                style={{
                                    backgroundColor: "var(--primary-dark)",
                                    backgroundImage: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-blue) 100%)",
                                    border: "1px solid var(--gold-light)"
                                }}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                            >
                                <div className="row align-items-center">
                                    <div className="col-lg-8">
                                        <div className="d-flex align-items-center gap-3 mb-3">
                                            <FontAwesomeIcon icon={faRibbon} style={{ color: "var(--gold)", fontSize: "1.5rem" }} />
                                            <h5 className="fw-bold mb-0" style={{ color: "var(--white)" }}>
                                                Recognition & Distinctions
                                            </h5>
                                        </div>
                                        <p className="mb-0" style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.95rem" }}>
                                            {partners[activePartner].id === 1
                                                ? "Bentham Scholar at University College London and extensive experience with leading law firms and major corporate clients."
                                                : "Marchant Foundation Scholar at London School of Economics and distinguished career with prestigious law chambers."}
                                        </p>
                                    </div>
                                    <div className="col-lg-4 text-center">
                                        <motion.div
                                            className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: "rgba(184, 134, 11, 0.2)",
                                                border: "2px solid var(--gold)"
                                            }}
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                        >
                                            <FontAwesomeIcon icon={faStar} style={{ color: "var(--gold)", fontSize: "2rem" }} />
                                        </motion.div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>

                {/* Team Stats */}
                {/* <motion.div
                    className="row g-4 mt-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="col-12">
                        <div className="text-center mb-5">
                            <h3 className="fw-bold mb-3"
                                style={{
                                    fontFamily: "'Playfair Display', serif",
                                    color: "var(--primary-dark)"
                                }}
                            >
                                Collective Expertise
                            </h3>
                        </div>

                        <div className="row g-4">
                            {[
                                { label: "Combined Experience", value: "36+", suffix: "Years", icon: faCalendarAlt },
                                { label: "London Educated", value: "100%", suffix: "Partners", icon: faUniversity },
                                { label: "Cases Handled", value: "5000+", suffix: "Across Courts", icon: faGavel },
                                { label: "Corporate Clients", value: "18+", suffix: "Fortune 500", icon: faBuilding }
                            ].map((stat, index) => (
                                <motion.div key={index} className="col-6 col-md-3" variants={itemVariants}>
                                    <HoverCard
                                        className="text-center p-4 rounded-4 h-100"
                                        style={{
                                            backgroundColor: "var(--white)",
                                            border: "1px solid var(--gold-light)",
                                            boxShadow: "var(--shadow-soft)"
                                        }}
                                    >
                                        <motion.div
                                            className="mb-3"
                                            whileHover={{ scale: 1.1, rotate: 5 }}
                                        >
                                            <FontAwesomeIcon
                                                icon={stat.icon}
                                                style={{
                                                    color: "var(--gold)",
                                                    fontSize: "1.8rem"
                                                }}
                                            />
                                        </motion.div>
                                        <div className="display-6 fw-bold mb-1"
                                            style={{
                                                color: "var(--gold)",
                                                fontFamily: "'Playfair Display', serif"
                                            }}
                                        >
                                            {stat.value}
                                        </div>
                                        <div className="fw-semibold mb-1" style={{ color: "var(--primary-dark)" }}>
                                            {stat.label}
                                        </div>
                                        <div className="small" style={{ color: "var(--gray-medium)" }}>
                                            {stat.suffix}
                                        </div>
                                    </HoverCard>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div> */}
            </div>
        </div>
    );
};